import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  FormHelperText,
  Alert,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  Share as ShareIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  AdminPanelSettings as AdminIcon,
  PersonAdd as PersonAddIcon,
} from "@mui/icons-material";
import { projectsApi } from "../services/rag-service";
import { useTranslation } from "react-i18next";

/**
 * Reusable ShareDialog component for sharing collections and LLM configs
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Function to call when the dialog is closed
 * @param {Function} props.onSubmit - Function to call when the form is submitted (email, accessLevel) => void
 * @param {string} props.title - Title for the dialog (e.g., "Share Collection: My Collection")
 * @param {string} props.itemType - Type of item being shared (e.g., "Collection", "Model")
 * @param {boolean} props.isSubmitting - Whether the form is currently submitting
 * @param {string} props.error - Error message to display if any
 * @param {string} props.projectId - Project ID that the item belongs to (for collections)
 * @returns {JSX.Element} The ShareDialog component
 */
const ShareDialog = ({
  open,
  onClose,
  onSubmit,
  title,
  itemType = "Item",
  isSubmitting = false,
  error = "",
  projectId = null,
  name = "",
}) => {
  const { t } = useTranslation();
  const normalizedItemType = itemType.toLowerCase();
  const translatedItemType =
    normalizedItemType === "collection"
      ? t("shareDialog.collection")
      : normalizedItemType === "model"
        ? t("shareDialog.model")
        : normalizedItemType === "project"
          ? t("shareDialog.project")
          : itemType.toLowerCase();
  const translatedItemTypeReference =
    normalizedItemType === "collection"
      ? t("shareDialog.thisCollection")
      : normalizedItemType === "model"
        ? t("shareDialog.thisModel")
        : normalizedItemType === "project"
          ? t("shareDialog.thisProject")
          : translatedItemType;
  const [email, setEmail] = useState("");
  const [accessLevel, setAccessLevel] = useState("viewer");
  const [emailError, setEmailError] = useState("");
  const [userMembershipStatus, setUserMembershipStatus] = useState(null);
  const [invitingToProject, setInvitingToProject] = useState(false);
  const [projectInviteSuccess, setProjectInviteSuccess] = useState(false);

  const handleClose = () => {
    if (isSubmitting || invitingToProject) return;

    // Reset form
    setEmail("");
    setAccessLevel("viewer");
    setEmailError("");
    setUserMembershipStatus(null);
    setProjectInviteSuccess(false);

    // Call the parent's onClose function
    onClose();
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Check if user is a member of the project
  const checkProjectMembership = async (emailAddress) => {
    if (!projectId || !emailAddress) return;

    try {
      // Directly check membership using email
      const membershipStatus = await projectsApi.checkUserProjectMembership(
        projectId,
        emailAddress,
      );

      setUserMembershipStatus(membershipStatus);
      return membershipStatus;
    } catch (error) {
      console.error("Error checking project membership:", error);
      // If there's an error (e.g., 404 Not Found), treat as not a member
      // or let the main submit handle potential API errors.
      // For now, let's assume an error means we can't confirm membership.
      setUserMembershipStatus({ is_member: false }); // Assume not member on error
      return { is_member: false };
    }
  };

  // Handle inviting the user to the project
  const handleInviteToProject = async () => {
    if (!projectId || !email || invitingToProject) return;

    setInvitingToProject(true);

    try {
      // Share the project with the user
      await projectsApi.shareProject(projectId, {
        email: email.trim(),
        access_level: accessLevel, // Use the same access level as for the collection
      });

      // Update state to show success message
      setProjectInviteSuccess(true);
      setUserMembershipStatus({ is_member: true, access_level: accessLevel });
    } catch (error) {
      console.error("Error inviting user to project:", error);
      setEmailError(error.message);
    } finally {
      setInvitingToProject(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Validate email
    if (!email.trim()) {
      setEmailError(t("shareDialog.emailRequired"));
      return;
    }

    if (!validateEmail(email)) {
      setEmailError(t("shareDialog.validEmailRequired"));
      return;
    }

    setEmailError("");

    // If we're sharing an item that belongs to a project (Collection or Model),
    // check membership first.
    if (
      (normalizedItemType === "collection" ||
        normalizedItemType === "model") &&
      projectId &&
      !userMembershipStatus
    ) {
      const membershipStatus = await checkProjectMembership(email.trim());

      // If already checked and the user is a member, proceed with sharing
      if (membershipStatus && membershipStatus.is_member === true) {
        onSubmit(email.trim(), accessLevel);
        return;
      }

      // If membership check completed and user is not a member, UI will show invitation option
      return;
    }

    // Call the parent's onSubmit function if no project checks needed
    // or if the user is already a member of the project
    onSubmit(email.trim(), accessLevel);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {title || t("shareDialog.shareAction", { name })}
        <IconButton
          aria-label={t("common.close")}
          onClick={handleClose}
          disabled={isSubmitting || invitingToProject}
          sx={{
            position: "absolute",
            insetInlineEnd: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <Typography variant="body2" gutterBottom>
            {t("shareDialog.shareWithDescription", {
              itemType: translatedItemTypeReference,
            })}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {error}
            </Alert>
          )}

          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label={t("common.emailAddress")}
            name="email"
            autoFocus
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailError("");
              // Reset membership status when email changes
              if (userMembershipStatus) {
                setUserMembershipStatus(null);
                setProjectInviteSuccess(false);
              }
            }}
            error={!!emailError}
            helperText={emailError}
            disabled={isSubmitting || invitingToProject}
            onBlur={() => {
              // Check membership only if it's a collection or model and a project ID is provided
              if (
                email &&
                validateEmail(email) &&
                projectId &&
                (normalizedItemType === "collection" ||
                  normalizedItemType === "model")
              ) {
                checkProjectMembership(email.trim());
              }
            }}
          />

          {/* Project membership warning and invite button */}
          {projectId &&
            userMembershipStatus &&
            userMembershipStatus.is_member === false &&
            !projectInviteSuccess && (
              <Alert
                severity="warning"
                sx={{ mt: 2, mb: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    startIcon={<PersonAddIcon />}
                    onClick={handleInviteToProject}
                    disabled={invitingToProject}
                  >
                    {invitingToProject
                      ? t("shareDialog.inviting")
                      : t("shareDialog.invite")}
                  </Button>
                }
              >
                {t("shareDialog.notProjectMember", {
                  itemType: translatedItemTypeReference,
                })}
              </Alert>
            )}

          {/* Project invitation success message */}
          {projectInviteSuccess && (
            <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
              {t("shareDialog.projectInviteSuccess", {
                itemType: translatedItemTypeReference,
              })}
            </Alert>
          )}

          <FormControl fullWidth margin="normal">
            <InputLabel id="access-level-label">
              {t("projects.accessLevel")}
            </InputLabel>
            <Select
              labelId="access-level-label"
              id="access-level"
              value={accessLevel}
              label={t("projects.accessLevel")}
              onChange={(e) => setAccessLevel(e.target.value)}
              disabled={isSubmitting || invitingToProject}
            >
              <MenuItem value="admin">
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <AdminIcon
                    fontSize="small"
                    sx={{ marginInlineEnd: 1, color: "primary.main" }}
                  />
                  <Box>
                    <Typography variant="body2">
                      {t("aiCatalog.admin")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("shareDialog.adminDescription")}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
              <MenuItem value="modifier">
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <EditIcon
                    fontSize="small"
                    sx={{ marginInlineEnd: 1, color: "success.main" }}
                  />
                  <Box>
                    <Typography variant="body2">
                      {t("aiCatalog.editor")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("shareDialog.editorDescription")}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
              <MenuItem value="viewer">
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <VisibilityIcon
                    fontSize="small"
                    sx={{ marginInlineEnd: 1 }}
                  />
                  <Box>
                    <Typography variant="body2">
                      {t("aiCatalog.viewer")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("shareDialog.viewerDescription")}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            </Select>
            <FormHelperText>
              {t("shareDialog.accessLevelHelper")}
            </FormHelperText>
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          disabled={isSubmitting || invitingToProject}
          color="secondary"
          variant="text"
        >
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={
            !email ||
            isSubmitting ||
            invitingToProject ||
            (projectId &&
              userMembershipStatus &&
              userMembershipStatus.is_member === false &&
              !projectInviteSuccess)
          }
          variant="contained"
          startIcon={
            isSubmitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <ShareIcon />
            )
          }
        >
          {isSubmitting
            ? t("shareDialog.sharing")
            : t("shareDialog.shareAction", { itemType: translatedItemType })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareDialog;
